import { useState } from 'react';
import SubmitButton from '../../components/submitButton';
import DatePicker from 'react-datepicker';
import SelectMulti from 'react-select';
import Layout from '../../components/layout';
import { useAsync } from 'react-async';
import { selectOption } from '../../types/selectOption';
import { downloadCostPerChildCustom, getCostPerChildOptions } from '../../api/services/costPerChildService';
import CostPerChildCustomRequest from '../../api/responses/costPerChildCustomRequest';
import { ReactSearchAutocomplete } from 'react-search-autocomplete';
import AutoCompleteOption from '../../api/models/autoCompleteModel';
import { getTherapistNamesAndIds } from '../../api/services/therapistService';
import { generateAutocompleteOptions } from '../../common/utils';
import moment from 'moment';

const fetchTherapists = async () => await getTherapistNamesAndIds();

const CostPerChild = () => {
    //filters
    const [startDate, setStartDate] = useState<Date>();
    const [endDate, setEndDate] = useState<Date>();
    const [districtId, setDistrictId] = useState<number>();
    const [divisionId, setDivisionId] = useState<number | undefined>();
    const [userId, setUserId] = useState<number | undefined>();
    const [disciplineId, setDisciplineId] = useState<number | undefined>();
    const [disciplineGroupId, setDisciplineGroupId] = useState<number | undefined>();
    const [ckyFlag, setCkyFlag] = useState<boolean | undefined>();
    const [payrateFlag, setPayRateFlag] = useState<boolean>(false);
    const [billrateFlag, setBillrateFlag] = useState<boolean>(false);
    const [paidMonthlyFlag, setPaidMonthlyFlag] = useState<boolean | undefined>();

    //options
    const [divisionOptions, setDivisionOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [districtOptions, setDistrictOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [disciplineOptions, setDisciplineOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [disciplineGroupOptions, setDisciplineGroupOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [therapistOptions, setTherapistOptions] = useState<AutoCompleteOption[]>([{ id: -1, name: '' }]);

    //fill the options
    const districtAsyc = useAsync({
        promiseFn: getCostPerChildOptions,
        onResolve: (u) => {
            if (u.data === undefined) {
                alert("There was an issue loading options, please refresh the page and try again");
                return;
            }
            let options = u.data;

            if (options.divisions !== undefined)
                setDivisionOptions(
                    options.divisions.map((d) => { return { label: d.name, value: d.divisionId ?? -1 } }).sort((a, b) => a.label.localeCompare(b.label))
                );

            if (options.districts !== undefined)
                setDistrictOptions(
                    options.districts.map((d) => { return { label: d.name, value: d.districtId ?? -1 } }).sort((a, b) => a.label.localeCompare(b.label))
                );

            if (options.disciplines !== undefined)
                setDisciplineOptions(
                    options.disciplines.map((d) => { return { label: d.name, value: d.disciplineId ?? -1 } }).sort((a, b) => a.label.localeCompare(b.label))
                );

            if (options.disciplinesGroups !== undefined)
                setDisciplineGroupOptions(
                    options.disciplinesGroups.map((d) => { return { label: d.name, value: d.disciplineGroupId ?? -1 } }).sort((a, b) => a.label.localeCompare(b.label))
                );
        },
    });

    //AutoComplete Methods 
    const autoCompleteTherapistSearch = (search: string, results: any) => {
        getTherapistsAutoCompleteAsync.run(search);
    }

    const autoCompleteTherapistSelect = (item: AutoCompleteOption) => {
        setUserId(item.id ?? undefined);
    }

    const getTherapistsAutoCompleteAsync = useAsync({
        promiseFn: fetchTherapists,
        onResolve: (u) => setTherapistOptions(generateAutocompleteOptions(u.data, 'value', 'label'))
    });

    const downloadReport = () => {
        let filter: CostPerChildCustomRequest = {
            districtId: districtId ?? null,
            startDate: startDate!,
            endDate: endDate!,
            userId: userId ?? null,
            divisionId: divisionId ?? null,
            disciplineId: disciplineId ?? null,
            disciplineGroupId: disciplineGroupId ?? null,
            ckyFlag: ckyFlag ?? null,
            payRatesMissing: payrateFlag,
            billRateMissing: billrateFlag,
            paidMonthlyFlag: paidMonthlyFlag ?? null
        }

        return downloadCostPerChildCustom(filter);
    }

    const downloadReportAsync = useAsync({
        deferFn: downloadReport
    })

    const parseBoolValue = (value: string) => {
        if (value === "-1")
            return undefined;

        return (value === "1");
    }

    return (
        <Layout isLoading={districtAsyc.isPending || downloadReportAsync.isPending}>
            <>
                {/* Search Fields */}
                <div className='z-0 px-4 py-5 bg-white sm:p-6'>
                    {/* Row 1 */}
                    <div className='z-0 grid grid-cols-6 gap-6'>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <label className='primary'>Start Date <span className='italic text-gray-600'>{"(Required)"}</span></label>
                            <DatePicker
                                selected={startDate}
                                onChange={(date: Date) => { setStartDate((date === null ? undefined : date)!); }}
                                dateFormat="MM/dd/yyyy"
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <label className='primary'>End Date <span className='italic text-gray-600'>{"(Required)"}</span></label>
                            <DatePicker
                                selected={endDate}
                                onChange={(date: Date) => { setEndDate((date === null ? undefined : date)!); }}
                                dateFormat="MM/dd/yyyy"
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <label className='primary'>Therapist</label>
                            <ReactSearchAutocomplete
                                key={"therapistAutoComplete"}
                                items={therapistOptions}
                                onSearch={autoCompleteTherapistSearch}
                                onSelect={autoCompleteTherapistSelect}
                                styling={{ borderRadius: "4px", height: "38px", zIndex: 1000 }}
                                onClear={() => { setUserId(undefined); }}
                                autoFocus
                            />
                        </div>

                        {/* Row 2 */}
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <label className='primary'>Division</label>
                            <SelectMulti
                                id='divisionPicker'
                                className='block primary'
                                isMulti={false}
                                options={divisionOptions}
                                isClearable={true}
                                onChange={(e: any | null) => {
                                    //Null exception handling
                                    e = (e !== null) ? e : { label: '', value: undefined };
                                    setDivisionId(Number(e.value));
                                }}
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <label className='primary'>District</label>
                            <SelectMulti
                                id='districtPicker'
                                className='block primary'
                                isMulti={false}
                                options={districtOptions}
                                isClearable={true}
                                onChange={(e: any | null) => {
                                    //Null exception handling
                                    e = (e !== null) ? e : { label: '', value: undefined };
                                    setDistrictId(Number(e.value));
                                }}
                            />
                        </div>
                        <br />

                        {/* Row 3 */}
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <label className='primary'>Discipline</label>
                            <SelectMulti
                                id='disciplinePicker'
                                className='block primary'
                                isMulti={false}
                                options={disciplineOptions}
                                isClearable={true}
                                onChange={(e: any | null) => {
                                    //Null exception handling
                                    e = (e !== null) ? e : { label: '', value: undefined };
                                    setDisciplineId(Number(e.value));
                                }}
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <label className='primary'>Discipline Group</label>
                            <SelectMulti
                                id='disciplineGroupPicker'
                                className='block primary'
                                isMulti={false}
                                options={disciplineGroupOptions}
                                isClearable={true}
                                onChange={(e: any | null) => {
                                    //Null exception handling
                                    e = (e !== null) ? e : { label: '', value: undefined };
                                    setDisciplineGroupId(Number(e.value));
                                }}
                            />
                        </div>
                        <br />

                        {/* Row 3 */}
                        <div className='flex col-span-6'>
                            <div className='flex-1 pr-3'>
                                <label className='primary'>CFY Flag</label>
                                <select
                                    onChange={(e) => { setCkyFlag(parseBoolValue(e.target.value)) }}>
                                    <option value="-1">Any</option>
                                    <option value="1">True</option>
                                    <option value="0">False</option>
                                </select>
                            </div>
                            <div className='flex-1 pr-3'>
                                <label className='primary'>Pay Rate</label>
                                <select
                                    onChange={(e) => { setPayRateFlag(parseBoolValue(e.target.value) ?? false) }}>
                                    <option value="0">All</option>
                                    <option value="1">Missing Rates</option>
                                </select>
                            </div>
                            <div className='flex-1 pr-3'>
                                <label className='primary'>Bill Rate</label>
                                <select
                                    onChange={(e) => { setBillrateFlag(parseBoolValue(e.target.value) ?? false) }}>
                                    <option value="0">All</option>
                                    <option value="1">Missing Rates</option>
                                </select>
                            </div>
                            <div className='flex-1'>
                                <label className='primary'>Paid Monthly</label>
                                <select
                                    onChange={(e) => { setPaidMonthlyFlag(parseBoolValue(e.target.value)) }}>
                                    <option value="-1">Any</option>
                                    <option value="1">Yes</option>
                                    <option value="0">No</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Button Row */}
                    <div className='grid grid-cols-2 gap-2 mt-6'>
                        <div className='col-span-1 sm:col-span-1 lg:col-span-1'>
                            <SubmitButton
                                text='Search'
                                className='mr-5 primary button'
                                onClick={() => { downloadReportAsync.run(); }}
                                disabled={!startDate || !endDate || !moment(startDate).isValid() || !moment(endDate).isValid()}
                            />
                        </div>
                    </div>
                </div>
            </>
        </Layout>
    );
};

export default CostPerChild;